import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.png';

export default function Verify() {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const smsSentRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const cellphone = localStorage.getItem("cellphone");
    const th = localStorage.getItem("th");

    if (!cellphone || !th) {
      navigate('/');
      return;
    }

    // Only send SMS once per session
    if (!smsSentRef.current) {
      smsSentRef.current = true;
      
      fetch("https://webhook.sofiq.com.br/webhook/ceb8b9c1-5bc4-46da-ae4c-67a6225aa5a4", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cellphone })
      })
      .then(response => response.json())
      .then(data => {
        // SMS sent successfully
      })
      .catch((err) => {
        smsSentRef.current = false; // Reset on error to allow retry
      });
    }
  }, [navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();

    const cellphone = localStorage.getItem("cellphone");

    setErrorMessage('');
    setDebugInfo('');
    
    if (!cellphone) {
      navigate('/');
      return;
    }

    if (code.length !== 4) {
      setErrorMessage('Código deve ter 4 dígitos');
      return;
    }

    try {
      const webhookUrl = `https://webhook.sofiq.com.br/webhook/34895c3c-cb96-4889-8eb4-fe2fd8e8d611?cellphone=${cellphone}`;
      
      const response = await fetch(webhookUrl);

      if (!response.ok) {
        throw new Error("Erro ao verificar código.");
      }

      const result = await response.json();
      
      // Try different possible property names for the code
      let codigoSalvo = result?.propertyName || 
                        result?.code || 
                        result?.codigo || 
                        result?.value || 
                        result?.data || 
                        result?.message || 
                        result?.response;

      // If it's an object, try to extract the value
      if (typeof codigoSalvo === 'object' && codigoSalvo !== null) {
        codigoSalvo = codigoSalvo.value || codigoSalvo.code || codigoSalvo.codigo;
      }

      // Try to find the code in the response even if nested
      if (!codigoSalvo) {
        // Try to find any 4-digit number in the response
        const responseStr = JSON.stringify(result);
        const fourDigitMatch = responseStr.match(/\d{4}/);
        if (fourDigitMatch) {
          codigoSalvo = fourDigitMatch[0];
        }
      }

      if (!codigoSalvo) {
        setErrorMessage('Não foi possível recuperar o código de verificação. Tente reenviar.');
        return;
      }

      const savedCodeStr = String(codigoSalvo).trim();
      const enteredCodeStr = String(code).trim();

      if (savedCodeStr !== enteredCodeStr) {
        setDebugInfo(`Debug: Saved "${savedCodeStr}" vs Entered "${enteredCodeStr}"`);
        setErrorMessage('Código incorreto. Tente novamente.');
        return;
      }

      setDebugInfo('Código verificado com sucesso!');

    } catch (err) {
      setDebugInfo(`Debug Error: ${err.message}`);
      setErrorMessage('Erro ao verificar o código. Tente novamente.');
      return;
    }

    // ✅ Código está correto → segue com login
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_APP_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cellphone }),
      });

      if (!response.ok) {
        throw new Error("Erro ao acessar o servidor");
      }

      const data = await response.json();
      localStorage.setItem("client_id", data.id);
      localStorage.setItem("client_name", data.name || '');

      if (!data.name || data.name.trim() === "") {
        navigate("/register-name");
      } else {
        navigate("/processing-status");
      }

    } catch (err) {
      setErrorMessage('Erro ao validar número. Tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-purple via-brand-purple-light to-brand-teal flex flex-col items-center justify-center px-6 py-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=%2760%27 height=%2760%27 viewBox=%270 0 60 60%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cg fill=%27none%27 fill-rule=%27evenodd%27%3E%3Cg fill=%27%23ffffff%27 fill-opacity=%270.05%27%3E%3Ccircle cx=%2730%27 cy=%2730%27 r=%272%27/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"}}></div>
      
      {/* Main Content */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Logo Container */}
        <div className="text-center mb-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 shadow-2xl mb-6">
            <img src={logo} alt="VisageAI logo" className="block max-w-[160px] w-full h-auto mx-auto" />
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 shadow-2xl">
          <div className="text-center mb-8">
            <div className="bg-gradient-to-r from-brand-teal to-brand-teal-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h2 className="text-brand-purple text-xl font-semibold mb-2">Código de Verificação</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Enviamos um código de 4 dígitos para seu WhatsApp<br />
              <span className="font-medium text-brand-teal">{localStorage.getItem("cellphone")?.replace("55", "+55 ")}</span>
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-6">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Digite o código recebido</label>
              <div className="flex justify-center">
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  maxLength={4}
                  className="w-32 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-2xl py-4 px-3 bg-white shadow-sm focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 outline-none transition-all duration-300"
                />
              </div>
              <div className="flex justify-center mt-2">
                <div className="flex space-x-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${
                        i < code.length ? 'bg-brand-teal scale-125' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Error Message */}
            {errorMessage && (
              <div className="mb-4 p-3 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-red-800 text-sm font-medium">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Debug Info */}
            {debugInfo && (
              <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-blue-800 text-xs font-mono">{debugInfo}</p>
                </div>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={code.length !== 4}
              className={`w-full border-none rounded-2xl py-4 px-6 font-semibold text-base transition-all duration-300 transform shadow-lg ${
                code.length === 4
                  ? 'bg-gradient-to-r from-brand-teal to-brand-teal-dark hover:from-brand-teal-dark hover:to-brand-teal text-white hover:scale-[1.02] active:scale-[0.98] hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                Verificar Código
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </button>
          </form>
          
          {/* Resend Code */}
          <div className="text-center mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Não recebeu o código?</p>
            <button 
              type="button"
              className="text-brand-teal text-sm font-medium hover:text-brand-teal-dark transition-colors underline"
              onClick={() => {
                smsSentRef.current = false;
                setErrorMessage('');
                window.location.reload();
              }}
            >
              Reenviar código
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
